/**
 * One SESSION of the rule sheet: the exercise it opened on, the staged copy it is editing, and the
 * two acts that end it.
 *
 * ── Why the session is a hook and not a store ───────────────────────────────────────────────
 * `progressionDisclosure` already owns WHICH sheet is out, because four surfaces inside a card need
 * that answer. What the open sheet is currently typing is nobody else's business: it lives and dies
 * with the sheet, one at a time, and putting it in a store would make a cancelled edit a thing the
 * app has to remember to forget.
 *
 * ── The three writes this sheet makes, and why they are three ────────────────────────────────
 * · **The RULE and its band** are staged here and committed once, through `applyExerciseAuthoring`.
 *   Picking a family, naming the column the client climbs and typing a step is three clicks and ONE
 *   prescription — see `model/progressionStaging.ts` for the whole argument.
 * · **A PIN** goes straight to the draft through `setMatrixOverride`. It is not staged, and that is
 *   deliberate rather than an omission: `setMatrixOverride` is the app's ONE pin writer, it is told
 *   which column the grid drew the cell in (`MatrixCellAddress.field`) and resolves the axis itself,
 *   and it carries the guard that makes a value equal to the rule's own projection CLEAR the cell
 *   rather than pin it. Staging pins would be a second writer without that guard, which is the
 *   duplication this repo's rules exist to prevent. The mock gives pins their own „Wyczyść
 *   nadpisania" in the foot, which is the same reading: a pin is its own act.
 * · **A set's ROLE** goes straight through `changeSetType`, for the same reason — the set table two
 *   boxes away already authors roles, and a second role writer would be the same defect again.
 *
 * The consequence, stated because a reader will otherwise find it surprising: „Anuluj" discards the
 * staged RULE and leaves pins and role changes standing. They were never staged, they each have
 * their own undo entry, and the foot's „Wyczyść nadpisania" is how pins are taken back.
 *
 * ── The session hands out BOTH copies, because the grid needs both ──────────────────────────
 * `stored` carries the pins and the set roles, which are written straight through and keep moving
 * while the sheet is open; `staged` carries the rule being authored and was seeded at the opening.
 * The grid is about the plan UNDER that rule, so `ProgressionSheet` reads the two together through
 * `stagedProjection` — neither copy alone is its subject. Until 15 September 2026 the grid drew
 * `stored` alone and a note above it explained why; the owner withdrew that, and the pin's own
 * address now carries the column so the grid is free to follow the chips.
 */

import { useMemo, useState } from 'react';

import {
  applyExerciseAuthoring,
  findExerciseLifespan,
  openingAxisField,
  readPlanDraft,
  useConsumedCycleCount,
  useExerciseDayClosedCycles,
  useExerciseDraft,
  usePlanDraftRevision,
  type CycleLifespan,
  type ExerciseDraft,
} from '~/entities/plan-draft';
import type { MeasurementFieldKey } from '~/shared/measurement';

import { executionsToMatrixInputs, type LiveMatrixInput } from '../model/executionsToMatrixInputs';

import {
  stagedCommits,
  stagedReducer,
  stagingIsDirty,
  type ProgressionStagingAction,
  type StagedBand,
} from '../model/progressionStaging';

export interface ProgressionSheetSession {
  /** The exercise as the PLAN holds it — what the matrix draws. */
  readonly stored: ExerciseDraft;
  /** The exercise as the sheet is authoring it — what the sentence and the chart draw. */
  readonly staged: ExerciseDraft;
  /** The cycles this exercise is prescribed in, holes and all. The matrix's columns. */
  readonly lifespan: CycleLifespan;
  readonly consumedCycles: number;
  /**
   * What the client has already been given at this slot and what they did with it, or `null` on a
   * template — the one input that turns the grid and the plot into the mock's LIVE variant.
   *
   * Read off `stored` rather than `staged`: the record is the SERVER's answer and the staged copy is
   * a rule the trainer has not applied yet, so a sheet open while another surface reloads the plan
   * must still draw what the client actually did.
   */
  readonly live: LiveMatrixInput | null;
  /** The column whose rule the sheet is editing. */
  readonly field: MeasurementFieldKey;
  readonly selectColumn: (field: MeasurementFieldKey) => void;
  readonly dispatch: (action: ProgressionStagingAction) => void;
  /** Remember that the range field was edited, so the commit carries the band it holds. */
  readonly noteBandEdit: (edit: StagedBand | null) => void;
  readonly isDirty: boolean;
  /** Commit the staged rule and band as ONE draft operation. */
  readonly apply: () => void;
}

/**
 * The session for one exercise. The caller mounts this component under a `key` of the slot token, so
 * the staged copy is seeded once per opening and a different exercise never inherits one.
 *
 * Returns `null` when the token names nothing — a day removed under an open sheet.
 */
export function useProgressionSheet(exerciseSlotToken: string): ProgressionSheetSession | null {
  const stored = useExerciseDraft(exerciseSlotToken);
  const revision = usePlanDraftRevision();
  const consumedCycles = useConsumedCycleCount();
  const closedCycles = useExerciseDayClosedCycles(exerciseSlotToken);

  // Seeded ONCE, at the opening: this is the state „Anuluj" puts back and the state dirtiness is
  // measured against. `stored` moves under it whenever anything else in the plan is edited.
  const [snapshot] = useState<ExerciseDraft | null>(stored);
  const [staged, setStaged] = useState<ExerciseDraft | null>(stored);
  const [field, setField] = useState<MeasurementFieldKey | null>(null);
  const [bandEdit, setBandEdit] = useState<StagedBand | null>(null);

  /**
   * THE ONE HAND-MEMO ON THIS SURFACE, and it buys correctness rather than speed. `findExerciseLifespan`
   * returns a fresh object, so it cannot be a store selector — Zustand v5 reads a selector inside
   * `getSnapshot`, and one minting an object on every call reports a changed snapshot forever.
   * Reading the singleton in render instead needs the compiler told that the answer moves: nothing
   * in the expression says so, and a derivation from `readPlanDraft()` grouped into a props-keyed
   * memo scope has frozen a surface in this app before (`useDayEditor`, 15 August 2026).
   *
   * `react-hooks/exhaustive-deps` calls `revision` unnecessary here and it is WRONG — the value is
   * not read in the body, but the store it stands for is. **Do not tidy it away, and do not silence
   * it with an `eslint-disable`**: measured on 15 August 2026, an `eslint-disable` of ANY react-hooks
   * rule inside a hook opts that hook out of the React Compiler entirely, silently, with the build
   * green. A visible warning carrying this note is the honest version of that trade.
   */
  const lifespan = useMemo<CycleLifespan>(
    () => findExerciseLifespan(readPlanDraft()?.phases ?? [], exerciseSlotToken) ?? { cycles: [] },
    [revision, exerciseSlotToken],
  );

  if (stored === null || snapshot === null || staged === null) return null;

  const viewed = field ?? openingAxisField(staged.progressionRules, staged.measurementFields);

  return {
    stored,
    staged,
    lifespan,
    consumedCycles,
    live: executionsToMatrixInputs({
      executions: stored.executions,
      lifespan: lifespan.cycles,
      closedCycles,
      consumedCycles,
    }),
    field: viewed,
    selectColumn: setField,
    dispatch: (action) => {
      setStaged((current) => (current === null ? current : stagedReducer(current, action)));
    },
    noteBandEdit: setBandEdit,
    isDirty: stagingIsDirty(snapshot, staged),
    /**
     * Commit EVERY column this session changed, not the one that happens to be on screen — and hand
     * the band to the one committed rule that bands its column. `stagedCommits` is where both of
     * those decisions live, and where a test can reach them.
     */
    apply: () => {
      applyExerciseAuthoring(exerciseSlotToken, stagedCommits(snapshot, staged, bandEdit));
    },
  };
}

